import type { ProductSchema } from "@labeling-tools/api/atlas";
import type {
  FeEmptyArea,
  FePerson,
  IdxToFeEmptyArea,
  SelectedProduct,
} from "@/store/useCoverageBetaStore";

/** State needed to render a completed coverage-beta task read-only. */
export interface CoverageBetaHydration {
  idxToFeEmptyArea: IdxToFeEmptyArea;
  persons: FePerson[];
  basePlanogramIds: string[];
  overridePlanogramIds: Record<number, string>;
  extraPlanogramIds: string[];
  boxIdxToSlot: Record<number, number>;
  activeEmptyAreaIdx: number;
}

/**
 * The completed-task `result` payload (shape produced by
 * `buildCoverageBetaResult`). Kept loose (`unknown`) — it arrives from the API
 * as `Record<string, unknown>` — and read defensively.
 */
interface OosConfirmation {
  query_empty_area_index?: number;
  confirmation_product_id?: string | null;
}

const asArray = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

/**
 * Reconstruct the coverage-beta store state from a completed task's `result`
 * (what the labeler submitted) plus the parcel `query` (the original auto
 * detections, needed to render deleted boxes and the planogram slots).
 *
 * Products are stored as `source: "planogram"` regardless of how they were
 * originally picked: the result only records product ids, and every read-only
 * consumer (summary thumbnails, footer, planogram + reference-box highlights)
 * keys off `product.id`, so the reference/planogram distinction — which only
 * affects the (disabled) confirm step and submit payload — is irrelevant here.
 *
 * @param result       the completed task `result`
 * @param query        `parcelResp.query`
 * @param productsById full product details resolved from the selected ids
 */
export const buildCoverageBetaHydration = (
  result: Record<string, unknown>,
  query: Record<string, unknown> | undefined,
  productsById: Map<string, ProductSchema>,
): CoverageBetaHydration => {
  const emptyAreas = asArray<{
    id?: string;
    points?: number[][];
    class_index?: number;
    confidence?: number;
    image_id?: string | null;
  }>(result.empty_areas);
  const sources = asArray<string>(result.empty_areas_source);
  const toAisle = asArray<number>(result.empty_area_to_aisle);
  const productSelections = asArray<{
    product_ids?: string[];
    planogram_id?: string;
  }>(result.product_selections);
  const oosConfirmations = asArray<OosConfirmation>(
    result.out_of_stock_confirmations,
  );
  const deleted = asArray<number>(result.deleted_empty_areas).map(Number);
  const overrides = (result.override_planogram_ids ?? {}) as Record<
    string,
    string
  >;

  const autoDetections = asArray<{
    id?: string;
    points?: number[][];
    class_index?: number;
    confidence?: number;
  }>(query?.empty_area_detections);

  const basePlanogramIds = asArray<string | null>(
    query?.aisle_to_planogram,
  ).map((p) => p ?? "");

  const confirmationByIndex = new Map<number, OosConfirmation>();
  oosConfirmations.forEach((c) => {
    if (typeof c.query_empty_area_index === "number") {
      confirmationByIndex.set(c.query_empty_area_index, c);
    }
  });

  const buildSelectedProduct = (
    id: string,
    planogramId: string,
  ): SelectedProduct => {
    const product = productsById.get(id);
    return {
      ...(product ?? {}),
      id,
      source: "planogram",
      planogram_id: planogramId,
    } as SelectedProduct;
  };

  // Submitted (non-deleted) areas keep, in order, the original auto-detection
  // indices for the auto-sourced ones (so reference-match badges still line up);
  // human-drawn areas get fresh ids after the auto range.
  const deletedSet = new Set(deleted);
  const nonDeletedAutoIdxs = autoDetections
    .map((_, i) => i)
    .filter((i) => !deletedSet.has(i));

  const idxToFeEmptyArea: IdxToFeEmptyArea = {};
  const boxIdxToSlot: Record<number, number> = {};
  const humanBaseIdx = autoDetections.length;
  let autoCursor = 0;
  let humanCursor = 0;

  emptyAreas.forEach((area, pos) => {
    const source: FeEmptyArea["source"] =
      sources[pos] === "human" ? "human" : "auto";
    let feIdx: number;
    if (source === "auto") {
      feIdx = nonDeletedAutoIdxs[autoCursor] ?? humanBaseIdx + pos;
      autoCursor += 1;
    } else {
      feIdx = humanBaseIdx + humanCursor;
      humanCursor += 1;
    }

    const sel = productSelections[pos];
    const productIds = (sel?.product_ids ?? []).filter(Boolean);
    const planogramId = sel?.planogram_id ?? "";
    const selectedProducts = productIds.map((id) =>
      buildSelectedProduct(id, planogramId),
    );
    const conf = confirmationByIndex.get(pos);

    idxToFeEmptyArea[feIdx] = {
      id: area.id ?? `area-${feIdx}`,
      points: area.points ?? [],
      class_index: area.class_index ?? 0,
      confidence: area.confidence ?? 1,
      source,
      feEmptyAreaIdx: feIdx,
      isDeleted: false,
      selectedProducts,
      isHidden: false,
      // A submitted area is reviewed: it has a product (oos_confirmed) or was
      // marked "no match" (empty_area). Either way it's valid.
      isValid: true,
      confirmedProductId: conf?.confirmation_product_id ?? null,
      // Grouped query images: the view it was drawn on (absent = primary).
      imageId: area.image_id ?? null,
    };
    boxIdxToSlot[feIdx] = typeof toAisle[pos] === "number" ? toAisle[pos] : 0;
  });

  // Deleted auto detections aren't in `empty_areas`; pull their geometry from
  // the parcel query so they still render (greyed) in the Deleted section.
  deletedSet.forEach((i) => {
    const det = autoDetections[i];
    if (!det) return;
    idxToFeEmptyArea[i] = {
      id: det.id ?? `area-${i}`,
      points: det.points ?? [],
      class_index: det.class_index ?? 0,
      confidence: det.confidence ?? 1,
      source: "auto",
      feEmptyAreaIdx: i,
      isDeleted: true,
      selectedProducts: [],
      isHidden: false,
      isValid: false,
      confirmedProductId: null,
    };
    boxIdxToSlot[i] = 0;
  });

  // Split override map into base-slot overrides vs. appended "extra" planograms
  // (whose slot index is >= basePlanogramIds.length), ordered by slot.
  const overridePlanogramIds: Record<number, string> = {};
  const extraEntries: [number, string][] = [];
  Object.entries(overrides).forEach(([slot, id]) => {
    const s = Number(slot);
    if (s < basePlanogramIds.length) overridePlanogramIds[s] = id;
    else extraEntries.push([s, id]);
  });
  extraEntries.sort((a, b) => a[0] - b[0]);
  const extraPlanogramIds = extraEntries.map(([, id]) => id);

  const persons: FePerson[] = asArray<{
    points?: number[][];
    confidence?: number;
    class_index?: number;
    image_id?: string | null;
  }>(result.unblurred_persons).map((p, i) => ({
    id: `person-${i}`,
    points: p.points ?? [],
    confidence: p.confidence ?? 1,
    class_index: p.class_index ?? 0,
    isDeleted: false,
    imageId: p.image_id ?? null,
  }));

  const firstActive = Object.values(idxToFeEmptyArea).find((a) => !a.isDeleted);

  return {
    idxToFeEmptyArea,
    persons,
    basePlanogramIds,
    overridePlanogramIds,
    extraPlanogramIds,
    boxIdxToSlot,
    activeEmptyAreaIdx: firstActive?.feEmptyAreaIdx ?? 0,
  };
};
